<template>
  <div class="pg-pair">
    <div class="p-field">
      <label class="p-field__label">Section Title</label>
      <input
        v-model="sectionData.content.sectionTitle"
        type="text"
        class="p-input"
        placeholder="섹션 제목을 입력하세요"
      />
    </div>

    <div class="pg-pair__list">
      <div
        v-for="(pair, index) in sectionData.content.data"
        :key="index"
        class="pg-pair__item"
      >
        <div class="pg-pair__item-head">
          <span class="pg-pair__index">{{ index + 1 }}</span>
          <div class="pg-pair__item-actions">
            <button
              type="button"
              class="pg-pair__icon-btn"
              title="위로 이동"
              :disabled="index === 0"
              @click.prevent="moveUp(index)"
            >
              <i class="material-icons-round">arrow_upward</i>
            </button>
            <button
              type="button"
              class="pg-pair__icon-btn"
              title="아래로 이동"
              :disabled="index === sectionData.content.data.length - 1"
              @click.prevent="moveDown(index)"
            >
              <i class="material-icons-round">arrow_downward</i>
            </button>
            <button
              type="button"
              class="pg-pair__icon-btn pg-pair__icon-btn--danger"
              title="제거"
              @click.prevent="removeItem(index)"
            >
              <i class="material-icons-round">delete</i>
            </button>
          </div>
        </div>

        <div class="pg-pair__item-body">
          <div class="pg-pair__fields">
            <div class="p-field">
              <label class="p-field__label">Term Text</label>
              <input
                v-model="pair.term_text"
                type="text"
                class="p-input"
                placeholder="용어"
              />
            </div>
            <div class="p-field">
              <label class="p-field__label">Definition</label>
              <input
                v-model="pair.definition"
                type="text"
                class="p-input"
                placeholder="정의"
              />
            </div>
          </div>
          <div class="pg-pair__image">
            <label class="p-field__label">Definition Image</label>
            <image-upload
              :default-image="pair.definition_image || require('@/assets/img/no_image.png')"
              :unique-id="'pair-definition-image-' + index"
              v-model="pair.definition_image"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="pg-pair__actions">
      <ABtn kind="yellow" icon="add" @click.prevent="addPair">Pair 추가</ABtn>
      <ABtn kind="ghost" icon="upload_file" @click.prevent="triggerExcelUpload">Excel 일괄 등록</ABtn>
      <input
        ref="excelFileInput"
        type="file"
        accept=".xlsx, .xls"
        class="pg-pair__file"
        @change="handleExcelUpload"
      />
    </div>
  </div>
</template>

<script setup>
import { defineModel, ref } from "vue";
import ImageUpload from "@/components/ImageUpload.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import * as XLSX from "xlsx";

const sectionData = defineModel({ type: Object, required: true });
const excelFileInput = ref(null);

const addPair = () => {
  sectionData.value.content.data.push({ term_text: "", definition: "", definition_image: "" });
};

const removeItem = (index) => {
  sectionData.value.content.data.splice(index, 1);
};

const moveUp = (index) => {
  if (index === 0) return;
  const data = sectionData.value.content.data;
  const temp = data[index];
  data[index] = data[index - 1];
  data[index - 1] = temp;
};

const moveDown = (index) => {
  const data = sectionData.value.content.data;
  if (index === data.length - 1) return;
  const temp = data[index];
  data[index] = data[index + 1];
  data[index + 1] = temp;
};

const triggerExcelUpload = () => {
  excelFileInput.value.click();
};

const handleExcelUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    let addedCount = 0;
    jsonData.forEach((row) => {
      if (!row || row.length === 0) return;
      const termText = row[0] ? String(row[0]).trim() : "";
      const definition = row[1] ? String(row[1]).trim() : "";
      if (termText || definition) {
        sectionData.value.content.data.push({
          term_text: termText,
          definition: definition,
          definition_image: "",
        });
        addedCount++;
      }
    });
    alert(`${addedCount}개의 항목을 추가했습니다.`);
    event.target.value = "";
  } catch (error) {
    console.error("Excel 파일 처리 오류:", error);
    alert("Excel 파일을 읽는 중 오류가 발생했습니다. 파일 형식을 확인해주세요.");
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-pair {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    min-width: 28px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 800;
    display: inline-grid;
    place-items: center;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover:not(:disabled) {
      background: $p-panel-alt;
      color: $p-ink;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &--danger {
      color: $p-err;

      &:hover:not(:disabled) {
        background: $p-err-soft;
        color: $p-err;
      }
    }
  }

  &__item-body {
    display: grid;
    grid-template-columns: 1fr 200px;
    gap: 16px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__image {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__file {
    display: none;
  }
}
</style>
